import qs from 'qs';

import { ServiceError } from 'src/lib/errors';
import { config } from 'src/lib/config';
import { normalizeEndDate, normalizeStartDate } from 'src/components/SetsConfigDetails/dateOverlap';
import { extractNewnessFromSets } from 'src/components/SetsConfigDetails/newnessSet';
import { extractSeasonalityFromSets } from 'src/components/SetsConfigDetails/seasonalitySet';
import { AbstractService } from 'src/services/AbstractService';
import {
    getMockSetsConfigsPage, getMockSetsConfig, createMockSetsConfig, updateMockSetsConfig, deleteMockSetsConfig,
} from 'src/data/mockSetsConfigs';

export const DEFAULT_SETS_CONFIG_LIMIT = 20;

const USE_MOCK = false; // flip to true to use mock data instead of calling the ICS service

type CultureGroupLike = Partial<Models.IngestConfigApi.SetsConfigs.SetsConfigCultureGroup> & {
    locales?: string[];
};

const normalizeCultureGroup = (
    group: CultureGroupLike | undefined,
): Models.IngestConfigApi.SetsConfigs.SetsConfigCultureGroup => {
    const normalizedSets = (group?.sets ?? []).map((set) => ({
        ...(set as Models.SetsPlayground.SetDefinition),
        name: set?.name ?? '',
    }));
    const {
        newnessEnabled, newnessLookbackDays, newnessDistribution, sets: setsWithoutNewness,
    } = extractNewnessFromSets(normalizedSets);
    const {
        seasonalityEnabled, seasonalityDistribution, sets,
    } = extractSeasonalityFromSets(setsWithoutNewness);

    return {
        name: group?.name ?? '',
        cultures: group?.cultures ?? group?.locales ?? [],
        startDate: normalizeStartDate(group?.startDate),
        endDate: normalizeEndDate(group?.endDate),
        newnessEnabled,
        newnessLookbackDays,
        newnessDistribution,
        seasonalityEnabled,
        seasonalityDistribution,
        notes: group?.notes ?? '',
        sets,
    };
};

const normalizeSetsConfig = (
    productSetsConfig: Partial<Models.IngestConfigApi.SetsConfigs.ProductSetsConfig> | null,
): Models.IngestConfigApi.SetsConfigs.ProductSetsConfig | null => {
    if (!productSetsConfig) {
        return null;
    }

    return {
        productKey: productSetsConfig.productKey ?? '',
        productName: productSetsConfig.productName ?? '',
        cultureGroups: (productSetsConfig.cultureGroups ?? []).map((group) => normalizeCultureGroup(group)),
        modifiedDate: productSetsConfig.modifiedDate ?? new Date(0).toISOString(),
        modifiedBy: productSetsConfig.modifiedBy ?? '',
        isEnabled: productSetsConfig.isEnabled ?? true,
    };
};

class SetsConfigApi extends AbstractService implements Services.SetsConfigApi.SetsConfigs {
    public async getSetsConfigs(
        bearerToken: string | undefined | false,
        search: App.SetsConfigs.Search,
        offset?: number,
        limit?: number,
        signal?: AbortSignal,
    ): Promise<Models.PageResult<Models.IngestConfigApi.SetsConfigs.SetsConfigSummary>> {
        if (USE_MOCK) {
            return getMockSetsConfigsPage(search, offset ?? 0, limit ?? DEFAULT_SETS_CONFIG_LIMIT);
        }

        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getSetsConfigs. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/setsConfigs`;
        const searchParams = qs.stringify({
            requestor: config.appName,
            search: search.searchTerm,
            offset,
            limit,
        });

        try {
            const response = await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.PageResult<Models.IngestConfigApi.SetsConfigs.SetsConfigSummary>>();

            return {
                ...response,
                results: response.results.map((result) => ({
                    ...result,
                    productName: result.productName ?? '',
                    cultureGroups: result.cultureGroups ?? [],
                    isEnabled: result.isEnabled ?? true,
                })),
            };
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get sets configs',
                info: (e as Error).message,
                url,
                searchParams,
            });
        }
    }

    public async getSetsConfig(
        bearerToken: string | undefined | false,
        productKey: string,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.SetsConfigs.ProductSetsConfig | null> {
        if (USE_MOCK) {
            return normalizeSetsConfig(await getMockSetsConfig(productKey));
        }

        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request getSetsConfig. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/setsConfigs/${productKey}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
            noCache: true,
        });

        try {
            const response = await this.api
                .get(url, {
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.SetsConfigs.ProductSetsConfig>();

            return normalizeSetsConfig(response);
        } catch (e: any) {
            if (e?.response?.status === 404) {
                return null;
            }
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to get sets config',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async createSetsConfig(
        bearerToken: string | undefined | false,
        productKey: string,
        productName: string,
        modifiedBy: string,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.SetsConfigs.ProductSetsConfig> {
        if (USE_MOCK) {
            const created = await createMockSetsConfig(productKey, productName, modifiedBy);

            return normalizeSetsConfig(created) as Models.IngestConfigApi.SetsConfigs.ProductSetsConfig;
        }

        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request createSetsConfig. No bearerToken provided.',
            });
        }

        // The ICS write is an upsert, so guard against overwriting an existing config for the product.
        const existing = await this.getSetsConfig(bearerToken, productKey, signal);

        if (existing) {
            throw new ServiceError({
                message: `A sets config already exists for ${productKey}.`,
            });
        }

        const url = `api/${this.version}/setsConfigs/${productKey}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        const body: Models.IngestConfigApi.SetsConfigs.ProductSetsConfig = {
            productKey,
            productName,
            cultureGroups: [],
            modifiedDate: new Date().toISOString(),
            modifiedBy,
            isEnabled: false,
        };

        try {
            const response = await this.api
                .put(url, {
                    json: body,
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.SetsConfigs.ProductSetsConfig>();

            return normalizeSetsConfig(response) as Models.IngestConfigApi.SetsConfigs.ProductSetsConfig;
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to create sets config',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async updateSetsConfig(
        bearerToken: string | undefined | false,
        productKey: string,
        // Named to avoid shadowing the module-level `config` (src/lib/config) used below for
        // config.appName / this.version.
        productSetsConfig: Models.IngestConfigApi.SetsConfigs.ProductSetsConfig,
        signal?: AbortSignal,
    ): Promise<Models.IngestConfigApi.SetsConfigs.ProductSetsConfig> {
        if (USE_MOCK) {
            const updated = await updateMockSetsConfig(productKey, productSetsConfig);

            return normalizeSetsConfig(updated) as Models.IngestConfigApi.SetsConfigs.ProductSetsConfig;
        }

        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request updateSetsConfig. No bearerToken provided.',
            });
        }

        // Same upsert endpoint as create.
        const url = `api/${this.version}/setsConfigs/${productKey}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            const response = await this.api
                .put(url, {
                    json: productSetsConfig,
                    searchParams,
                    signal,
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                    },
                })
                .json<Models.IngestConfigApi.SetsConfigs.ProductSetsConfig>();

            return normalizeSetsConfig(response) as Models.IngestConfigApi.SetsConfigs.ProductSetsConfig;
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to update sets config',
                info: (e as Error).message,
                url,
            });
        }
    }

    public async deleteSetsConfig(
        bearerToken: string | undefined | false,
        productKey: string,
        signal?: AbortSignal,
    ): Promise<void> {
        if (USE_MOCK) {
            return deleteMockSetsConfig(productKey);
        }

        if (!bearerToken) {
            throw new ServiceError({
                message: 'Unable to perform request deleteSetsConfig. No bearerToken provided.',
            });
        }

        const url = `api/${this.version}/setsConfigs/${productKey}`;
        const searchParams = qs.stringify({
            requestor: config.appName,
        });

        try {
            return await this.api.delete(url, {
                searchParams,
                signal,
                headers: {
                    Authorization: `Bearer ${bearerToken}`,
                },
            })
                .json<void>();
        } catch (e) {
            throw new ServiceError({
                ...(e as Error),
                message: 'Failed to delete sets config',
                info: (e as Error).message,
                url,
            });
        }
    }
}

export const SetsConfigService = new SetsConfigApi(config.services.ingestConfigServiceApi);
